/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import reportInvoiceService from 'service/reportInvoice';
import legalEntitySettingsService from 'service/legalEntitySettings';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { fieldMappingIncome } from 'data/fieldMaping';

export type IncomeData = {
  product_name: string;
  barcode: string;
  warehouse: string;
  currency: string;
  unit: string;
  supplier: string;
  category: string;
  purchase_price: string;
  quantity: string;
  price_cost: string;
  paid: string;
  gross_income: string;
  date: string;
};

interface IncomeTableProps {
  generateTableData?: any;
  setGeneratedTableData?: any;
  getName?: any;
  getFilterData?: any;
  barcodeInputRef?: any;
  debouncedBarcode?: any;
  currentPage?: any;
  setCurrentPage?: any;
  pageCount?: any;
  setPageCount?: any;
  getBarcode?: any;
  setTotalincome?: any;
  setLabeledVisibleFields?: any;
}

const IncomeTable = ({
  generateTableData,
  setGeneratedTableData,
  getName,
  getFilterData,
  debouncedBarcode,
  currentPage,
  setCurrentPage,
  pageCount,
  setPageCount,
  getBarcode,
  setTotalincome,
  setLabeledVisibleFields
}: IncomeTableProps) => {
  const { t, i18n } = useTranslation();

  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_pid
  );

  const branchId = useSelector((state: any) => state?.auth?.legal_entity?.pid);

  const [hasReset, setHasReset] = useState(false);

  // Initialize today's date in milliseconds
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const todayEnd = new Date().setHours(0, 0, 0, 0);

  // Memoized onVisibleFieldsChange callback with shallow comparison
  const handleVisibleFieldsChange = useCallback(
    (visibleFields: { [key: string]: { hidden: boolean; order: number } }) => {
      const newLabeledFields = Object.keys(visibleFields)
        .filter(columnKey => !visibleFields[columnKey].hidden) // Only include non-hidden fields
        .map(columnKey => {
          const backendField = Object.keys(fieldMappingIncome).find(
            key => fieldMappingIncome[key].columnKey === columnKey
          );
          return backendField || null;
        })
        .filter((field): field is string => field !== null) // Filter out null values
        .join(','); // Join into a comma-separated string

      // Only update state if newLabeledFields is different
      setLabeledVisibleFields((prev: any) => {
        const isEqual = prev === newLabeledFields;
        if (!isEqual) {
          return newLabeledFields;
        }
        return prev;
      });
    },
    [fieldMappingIncome]
  );

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_REPORTS_INCOME', getName, debouncedBarcode],
    async () => {
      pageToOffset(currentPage, 10);

      const res = await reportInvoiceService
        .getReportIncomeList({
          limit: 10,
          offset: pageToOffset(currentPage, 10),
          currency_pid: Object.keys(getFilterData).length === 0 && currencyId,
          branch_pid: Object.keys(getFilterData).length === 0 && branchId,
          date_from: Object.keys(getFilterData).length === 0 && todayStart,
          date_to:
            Object.keys(getFilterData).length === 0 && todayEnd + 86400000,
          name: getName,
          barcode: debouncedBarcode,
          ...getFilterData
        })
        .then((res: any) => {
          setPageCount(Math.ceil(res?.count / 10));
          setTotalincome(res?.total_income_amount || 0);

          // if (getName === '' && barcodeInputRef?.current) {
          //   barcodeInputRef.current.focus();
          //   setTimeout(() => barcodeInputRef.current?.select(), 500);
          // }

          return res?.list;
        });
      return res;
    },
    {
      enabled:
        getName.length === 0 ||
        getName.length > 2 ||
        debouncedBarcode.length === 0 ||
        debouncedBarcode.length > 2 ||
        !!currencyId ||
        !!branchId ||
        !!getFilterData ||
        !!currentPage
    }
  );

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const loading = isLoading || isFetching;

  useMemo(() => {
    if (!data || data.length === 0) {
      setGeneratedTableData([]); // Set empty array if no data
      return;
    }
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    const resData =
      data?.map((items: any) => {
        const packageQuantity = items?.product?.package_quantity || 1;
        const quantity = items?.quantity
          ? Number(items?.quantity.toFixed(3))
          : 0;
        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = quantity % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return {
          product_name: items?.product?.name,
          barcode: items?.product?.barcode,
          warehouse: items?.product?.ware_house?.name,
          unit: items?.product?.nomenclature?.measure_unit?.name?.[
            i18n?.language
          ],
          currency: items?.currency?.name?.[i18n?.language],
          buyyer: items?.user?.first_name,
          category:
            items?.product?.nomenclature?.category?.name?.[i18n?.language],
          purchase_price: items?.price_in
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.price_in)
                .replace(/,/g, ' ')
            : 0,
          paid: items?.payment_amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.payment_amount)
                .replace(/,/g, ' ')
            : 0,
          discrepancy: items?.payment_difference
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.payment_difference)
                .replace(/,/g, ' ')
            : 0,
          price_cost: items?.price
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.price)
                .replace(/,/g, ' ')
            : 0,
          gross_income: items?.income_amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.income_amount)
                .replace(/,/g, ' ')
            : 0,
          total_payment_price: items?.total_payment_amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.total_payment_amount)
                .replace(/,/g, ' ')
            : 0,
          payment_procedure: items?.payment_order?.name?.[i18n?.language],
          supplier: items?.legal_entity?.name,
          quantity:
            items?.product?.nomenclature?.measure_unit_kind_pid === 1
              ? quantityDisplay
              : quantity,
          /* prettier-ignore */
          date: formatDateToTimeZone(items?.date_create, timeZone)
        };
      }) ?? [];

    if (resData) {
      setGeneratedTableData(resData);
    }

    return resData;
  }, [data, dataSettings, t, i18n?.language]);

  const incomeDataTableColumns: ColumnDef<IncomeData>[] = [
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10; // Calculate the order number based on the row index
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'warehouse',
      header: t('warehouse'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'branch',
      header: t('branch'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'supplier',
      header: t('supplier'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'category',
      header: t('category'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },
    {
      accessorKey: 'purchase_price',
      header: t('purchase_price'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'quantity',
      header: t('quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price_cost',
      header: t('selling_price'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'paid',
      header: t('paid'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'gross_income',
      header: t('gross_income'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'date',
      header: t('date_create'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateTableData,
    columns: incomeDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if ((getName || getBarcode) && !hasReset) {
      setCurrentPage(1);
      setHasReset(true); // Prevents multiple resets
    }
  }, [getName, getBarcode, hasReset]);

  useEffect(() => {
    refetch();
  }, [getName, debouncedBarcode, currentPage]);
  return (
    <AdvanceTableProvider {...table}>
      <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          isLoading={loading}
          onVisibleFieldsChange={handleVisibleFieldsChange}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </AdvanceTableProvider>
  );
};

export default IncomeTable;
